import { useLight } from "@Light/services/light";
import { useSearchParams } from "@Light/utils/context";
import { isFetchBaseQueryError } from "@Light/utils/errors";
import { useObservedRateGroup } from "@Light/utils/rateGroup";
import posthog from "posthog-js";
import { PostHogProvider } from "posthog-js/react";
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router-dom";

const posthogKey = import.meta.env.VITE_POSTHOG_KEY;

let initialized = false;

if (typeof window !== "undefined" && posthogKey) {
  posthog.init(posthogKey, {
    api_host: "https://us.i.posthog.com",
    person_profiles: "identified_only",
    capture_pageview: false,
    session_recording: {
      maskAllInputs: true,
      maskInputFn: (text, element) => {
        if (element?.dataset.record === "true") {
          return text;
        }
        return "*".repeat(text.trim().length);
      },
    },
  });

  initialized = true;
}

function logoutPosthog() {
  if (initialized) {
    posthog.reset();
  }
}

export function captureEvent(
  event: string,
  properties?: Record<string, string | number | boolean | null>,
) {
  if (initialized) {
    posthog.capture(event, properties);
  } else {
    console.log("captureEvent", event, properties);
  }
}

export type UseCaptureEventOptions = {
  event: string;
  condition: boolean;
  properties?: Record<string, string | number | boolean | null>;
};

/**
 * Captures an event off a condition rather than a callback.
 */
export function useCaptureEvent({
  event,
  condition,
  properties,
}: UseCaptureEventOptions) {
  const latest = useRef({ event, properties });
  useEffect(() => {
    latest.current = { event, properties };
  });

  useEffect(() => {
    if (!condition) return;
    captureEvent(latest.current.event, latest.current.properties);
  }, [condition]);
}

/**
 * Register properties that are automatically attached to every subsequently
 * captured event (persisted across the session, cleared on `posthog.reset()`).
 */
export function registerSuperProperties(
  properties: Record<string, string | number | boolean | null>,
) {
  if (initialized) {
    posthog.register(properties);
  } else {
    console.log("registerSuperProperties", properties);
  }
}

export type PostHogState = {
  attribution: string | undefined;
  setAttribution: (v: string | undefined) => void;
  planUUID: string | undefined;
  setPlanUUID: (v: string | undefined) => void;
  /** The term to preselect for `planUUID`, from the same link. */
  termMonths: number | undefined;
  setTermMonths: (v: number | undefined) => void;
  featureFlags: string[] | undefined;
  setFeatureFlags: (v: string[] | undefined) => void;
  useLocalStorage: boolean;
};

export const PostHogContext = createContext<PostHogState | undefined>(
  undefined,
);

type AnalyticsProviderProps = {
  children: ReactNode;
  useLocalStorage: boolean;
};

export function AnalyticsProvider({
  children,
  useLocalStorage,
}: AnalyticsProviderProps) {
  const location = useLocation();
  const light = useLight();
  const [attribution, setAttribution] = useState<string | undefined>(
    (useLocalStorage && localStorage.getItem("attribution")) || undefined,
  );
  const [planUUID, setPlanUUID] = useState<string | undefined>(
    (useLocalStorage && localStorage.getItem("planUUID")) || undefined,
  );
  const [termMonths, setTermMonths] = useState<number | undefined>(
    (useLocalStorage && Number(localStorage.getItem("termMonths"))) ||
      undefined,
  );
  const [featureFlags, setFeatureFlags] = useState<string[] | undefined>(
    undefined,
  );

  const {
    data: account,
    isError,
    error,
  } = light.endpoints.getAccount.useQueryState();

  const rateGroup = useObservedRateGroup(planUUID || undefined);

  // Track pageviews on route change
  useEffect(() => {
    if (initialized) {
      posthog.capture("$pageview", {
        $current_url: window.location.href,
      });
    }
  }, [location]);

  // Identify user on login
  useEffect(() => {
    const properties: Record<string, string | boolean> = {};
    if (!account) {
      return;
    }

    properties.email = account.email;
    properties.app_uuid = account.app.uuid;
    properties.app_name = account.app.name;
    properties.app_is_sandbox = account.app.is_sandbox;
    properties.organization_uuid = account.app.organization_uuid;
    properties.organization_name = account.app.organization_name;

    if (rateGroup) {
      properties.plan_uuid = rateGroup.uuid;
      properties.plan_name = rateGroup.name;
    }

    if (attribution) {
      // Keep setting "flow_attribution" as that's the legacy key. The
      // "attribution" key is the one I'm using going forward.
      properties.flow_attribution = attribution;
      properties.attribution = attribution;
    }

    // Add feature flags to properties
    if (featureFlags) {
      featureFlags.forEach((flag) => {
        properties[`feature_flag_${flag.toLowerCase()}`] = true;
      });
    }

    const identity = account?.uuid ?? posthog.get_distinct_id();
    posthog.identify(identity, properties);
  }, [account, rateGroup, attribution, featureFlags]);

  // Log out user on logout
  useEffect(() => {
    if (isError && isFetchBaseQueryError(error) && error.status === 401) {
      logoutPosthog();
    }
  }, [isError, error]);

  // Detect tab/window close and capture event
  useEffect(() => {
    const handleBeforeUnload = () => {
      captureEvent("window_close");
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  return (
    <PostHogProvider client={posthog}>
      <PostHogContext.Provider
        value={{
          attribution,
          setAttribution,
          planUUID,
          setPlanUUID,
          termMonths,
          setTermMonths,
          featureFlags,
          setFeatureFlags,
          useLocalStorage,
        }}
      >
        {children}
      </PostHogContext.Provider>
    </PostHogProvider>
  );
}

function usePostHog(): PostHogState {
  const postHog = useContext(PostHogContext);
  if (!postHog) {
    throw new Error("must be wrapped in an AnalyticsProvider");
  }
  return postHog;
}

export function useAnalytics() {
  const postHog = usePostHog();
  const { useLocalStorage } = postHog;
  const setAttribution = (attr: string) => {
    postHog.setAttribution(attr);
    if (useLocalStorage) {
      localStorage.setItem("attribution", attr);
    }
  };
  const setPlanUUID = (uuid: string) => {
    postHog.setPlanUUID(uuid);
    if (useLocalStorage) {
      localStorage.setItem("planUUID", uuid);
    }
  };
  const setTermMonths = (term: number | undefined) => {
    postHog.setTermMonths(term);
    if (useLocalStorage) {
      if (term) {
        localStorage.setItem("termMonths", String(term));
      } else {
        localStorage.removeItem("termMonths");
      }
    }
  };
  const setFeatureFlags = (flags: string[]) => {
    postHog.setFeatureFlags(flags);
  };
  const attribution = postHog.attribution;
  const planUUID = postHog.planUUID;
  return {
    setAttribution,
    setPlanUUID,
    setTermMonths,
    setFeatureFlags,
    attribution,
    planUUID,
  };
}

export function useAnalyticsSearchParams() {
  const [searchParams] = useSearchParams();
  const attribution = searchParams.get("attribution");
  const plan = searchParams.get("plan");
  const termMonths = Number(searchParams.get("term_months")) || undefined;
  const { setAttribution, setPlanUUID, setTermMonths } = useAnalytics();

  useEffect(() => {
    if (attribution) {
      setAttribution(attribution);
    }
  }, [attribution, setAttribution]);

  useEffect(() => {
    if (plan) {
      setPlanUUID(plan);
      // A plan link without a term clears the term of an earlier link.
      setTermMonths(termMonths);
    }
  }, [plan, termMonths, setPlanUUID, setTermMonths]);
}

/** The term a link asked to preselect for its plan. It doesn't need an
 *  AnalyticsProvider, so screens rendered without one (stories) still work. */
export function useLinkedTerm(): { planUUID?: string; termMonths?: number } {
  const postHog = useContext(PostHogContext);
  return { planUUID: postHog?.planUUID, termMonths: postHog?.termMonths };
}
